import React, { useState } from 'react';
import { 
  CheckCircle, Package, Truck, Camera, AlertTriangle, 
  DollarSign, RotateCcw, Loader2, X, Check, ChevronRight,
  MapPin, Phone, Clock, CreditCard
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import PaymentLinkButton from '@/components/PaymentLinkButton';
import { useSettings } from '@/context/SettingsContext';
import { DateInput } from '@/components/ui/date-input';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const RESCHEDULE_REASONS = [
  { value: 'customer_not_available', label: 'Customer not available' },
  { value: 'customer_unreachable', label: 'Customer not reachable' },
  { value: 'customer_requested_later', label: 'Customer asked to deliver later' },
  { value: 'wrong_address', label: 'Wrong / incomplete address' },
  { value: 'driver_delayed', label: 'Driver delayed — could not reach in time' },
  { value: 'vehicle_breakdown', label: 'Vehicle breakdown / stuck on road' },
  { value: 'weather_road_blocked', label: 'Weather / road blocked' },
  { value: 'other', label: 'Other' },
];

const DriverActionPanel = ({ order, onOrderUpdate, canMarkFailed = true }) => {
  const { formatCurrency: formatCurrencyFromSettings } = useSettings();
  
  // Helper to format currency with order's currency
  const formatCurrency = (amount) => {
    return formatCurrencyFromSettings(amount, order?.currency);
  };
  
  const [loading, setLoading] = useState(false);
  const [showCashInput, setShowCashInput] = useState(false);
  const [cashAmount, setCashAmount] = useState('');
  const [showFailReason, setShowFailReason] = useState(false);
  const [failReason, setFailReason] = useState('');
  const [podImages, setPodImages] = useState([]);
  const [showReturnCollection, setShowReturnCollection] = useState(false);
  const [returnImages, setReturnImages] = useState([]);
  const [returnNotes, setReturnNotes] = useState('');
  const [collectedItems, setCollectedItems] = useState({});
  const [showPickConfirm, setShowPickConfirm] = useState(false);
  const [pickedItems, setPickedItems] = useState({});
  const [showPartial, setShowPartial] = useState(false);
  const [rejectedQty, setRejectedQty] = useState({});
  const [showDeliverConfirm, setShowDeliverConfirm] = useState(false);
  // GPS capture for delivery — required to confirm delivery
  const [capturingLocation, setCapturingLocation] = useState(false);
  const [deliveryLocation, setDeliveryLocation] = useState(null); // { lat, lng, accuracy }
  // Reschedule delivery
  const [showReschedule, setShowReschedule] = useState(false);
  const [reschedReason, setReschedReason] = useState('');
  const [reschedNote, setReschedNote] = useState('');
  const [reschedDate, setReschedDate] = useState('');

  if (!order) return null;

  const status = order.fulfillment_status;
  const isReturnOrder = ['return_replace', 'return_only', 'return_refund'].includes(order.return_replacement_type);
  const needsReturnCollection = isReturnOrder && !order.return_items_collected;
  // Partial link payments: collect only the remaining balance in cash
  const linkPaid = (order.payment_links || []).filter(l => l.is_paid).reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  // Partial delivery: customer pays only for accepted items
  const rejectedValue = Object.entries(rejectedQty).reduce((s, [idx, q]) => {
    const it = (order.items || [])[idx];
    return s + ((parseInt(q) || 0) * (parseFloat(it?.price) || 0));
  }, 0);
  const fullyPaid = order.payment_status === 'paid';
  const needsCash = !fullyPaid || (order.is_reopened && order.reopen_cash_required);
  const cashAmountDue = order.is_reopened
    ? order.reopen_cash_amount
    : Math.max(0, (parseFloat(order.total_amount) || 0) - linkPaid - rejectedValue);

  const handleDriverAction = async (action, extraData = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/orders/${order.id}/driver-action`,
        { action, ...extraData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      if (onOrderUpdate) {
        onOrderUpdate(response.data.order);
      }
      
      setShowCashInput(false);
      setCashAmount('');
      setPodImages([]);
      setShowFailReason(false);
      setFailReason('');
      setShowPickConfirm(false);
      setPickedItems({});
      setShowDeliverConfirm(false);
      setShowPartial(false);
      setRejectedQty({});
      setDeliveryLocation(null);
      setShowReschedule(false);
      setReschedReason('');
      setReschedNote('');
      setReschedDate('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  // Capture the driver's GPS location. Returns the coords object or
  // throws — caller decides how to surface the error.
  const captureDeliveryLocation = () => {
    return new Promise((resolve, reject) => {
      if (!('geolocation' in navigator)) {
        reject(new Error('GPS not supported on this device. Please use a phone with location services.'));
        return;
      }
      setCapturingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setCapturingLocation(false);
          const coords = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          };
          setDeliveryLocation(coords);
          resolve(coords);
        },
        (err) => {
          setCapturingLocation(false);
          let msg = 'Could not capture your location.';
          if (err.code === 1) msg = 'Location permission denied. Please enable GPS in your browser/device settings and try again.';
          else if (err.code === 2) msg = 'Location unavailable. Check that GPS is turned on.';
          else if (err.code === 3) msg = 'Getting your location timed out. Please try again with a clear view of the sky.';
          reject(new Error(msg));
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
      );
    });
  };

  const handleReturnCollection = async () => {
    if (returnImages.length === 0) {
      toast.error('Please take at least one photo of collected items');
      return;
    }

    const items = order.return_items_to_collect?.map(item => ({
      sku: item.sku,
      collected: collectedItems[item.sku] !== false
    })) || [];

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/orders/${order.id}/confirm-return-collection`,
        {
          collected_items: items,
          images: returnImages,
          notes: returnNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Return items collected!');
      if (onOrderUpdate) {
        onOrderUpdate(response.data.order);
      }
      setShowReturnCollection(false);
      setReturnImages([]);
      setReturnNotes('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e, type) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'pod') {
          setPodImages(prev => [...prev, reader.result].slice(0, 6));
        } else if (type === 'return') {
          setReturnImages(prev => [...prev, reader.result]);
        }
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeReturnImage = (index) => {
    setReturnImages(prev => prev.filter((_, i) => i !== index));
  };

  // Status indicator component
  const StatusBadge = () => {
    const statusConfig = {
      pending: { color: 'bg-yellow-500', text: 'Pending' },
      reopened: { color: 'bg-blue-500', text: 'Reopened' },
      ready_for_dispatch: { color: 'bg-blue-500', text: 'Ready to Pick' },
      assigned: { color: 'bg-blue-500', text: 'Assigned' },
      packed: { color: 'bg-amber-500', text: 'Packed' },
      accepted: { color: 'bg-purple-500', text: 'Accepted' },
      picked: { color: 'bg-indigo-500', text: 'Picked Up' },
      out_for_delivery: { color: 'bg-cyan-500', text: 'Out for Delivery' },
      on_the_way: { color: 'bg-cyan-500', text: 'On The Way' },
      delivered: { color: 'bg-green-500', text: 'Delivered' },
      failed: { color: 'bg-red-500', text: 'Failed' },
      delivery_rescheduled: { color: 'bg-amber-500', text: 'Rescheduled' },
    };
    const config = statusConfig[status] || { color: 'bg-gray-500', text: status };
    
    return (
      <div className={`${config.color} text-white px-4 py-2 rounded-full text-sm font-semibold text-center`}>
        {config.text}
      </div>
    );
  };

  // Progress steps indicator
  const ProgressSteps = () => {
    const steps = ['Accept', 'Pick Up', 'On The Way', 'Deliver'];
    
    // Calculate current step based on fulfillment status
    // Default to 0 for any unrecognized status
    let currentStep = 0;
    if (status === 'delivered' || status === 'fulfilled') {
      currentStep = 4; // All complete
    } else if (status === 'out_for_delivery' || status === 'on_the_way') {
      currentStep = 3;
    } else if (status === 'picked') {
      currentStep = 2;
    } else if (status === 'delivery_rescheduled') {
      currentStep = 2;
    } else if (status === 'accepted') {
      currentStep = 1;
    } else {
      // pending, reopened, ready_for_dispatch, assigned, null, undefined, etc.
      currentStep = 0;
    }

    return (
      <div className="flex items-center justify-between px-2 py-4">
        {steps.map((step, idx) => (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold
                ${idx < currentStep ? 'bg-green-500 text-white' : 
                  idx === currentStep ? 'bg-wt-accent text-white animate-pulse' : 
                  'bg-wt-surface text-wt-text-muted border-2 border-wt-border'}`}>
                {idx < currentStep ? <Check className="w-5 h-5" /> : idx + 1}
              </div>
              <span className={`text-xs mt-1 ${idx <= currentStep ? 'text-wt-text-primary font-medium' : 'text-wt-text-muted'}`}>
                {step}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`flex-1 h-1 mx-1 rounded ${idx < currentStep ? 'bg-green-500' : 'bg-wt-border'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  // Main action button based on status
  const renderActionButton = () => {
    const buttonBaseClass = "w-full py-6 text-xl font-bold rounded-2xl shadow-lg active:scale-95 transition-transform";
    
    switch (status) {
      case 'pending':
      case 'reopened':
      case 'ready_for_dispatch':
      case 'assigned':
      case 'packed':
        return (
          <button 
            onClick={() => handleDriverAction('accept')}
            disabled={loading}
            className={`${buttonBaseClass} bg-gradient-to-r from-blue-500 to-blue-600 text-white`}
            data-testid="driver-accept-order-btn"
          >
            {loading ? (
              <Loader2 className="w-8 h-8 animate-spin mx-auto" />
            ) : (
              <div className="flex items-center justify-center gap-3">
                <CheckCircle className="w-8 h-8" />
                <span>ACCEPT ORDER</span>
              </div>
            )}
          </button>
        );

      case 'accepted':
        if (!showPickConfirm) {
          return (
            <button 
              onClick={() => setShowPickConfirm(true)}
              disabled={loading}
              className={`${buttonBaseClass} bg-gradient-to-r from-purple-500 to-purple-600 text-white`}
            >
              {loading ? (
                <Loader2 className="w-8 h-8 animate-spin mx-auto" />
              ) : (
                <div className="flex items-center justify-center gap-3">
                  <Package className="w-8 h-8" />
                  <span>MARK AS PICKED</span>
                </div>
              )}
            </button>
          );
        }
        
        // Pick confirmation with item checkboxes
        return (
          <div className="space-y-4">
            <div className="bg-purple-500/10 rounded-2xl p-4 border-2 border-purple-500/30">
              <div className="flex items-center gap-3 mb-4">
                <Package className="w-8 h-8 text-purple-400" />
                <p className="text-lg font-bold text-wt-text-primary">Confirm Items Picked</p>
              </div>
              
              <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
                {order.items?.map((item, idx) => (
                  <label 
                    key={idx}
                    className="flex items-center gap-3 p-3 bg-wt-bg rounded-xl cursor-pointer active:scale-98"
                  >
                    <Checkbox
                      checked={pickedItems[item.sku || idx] !== false}
                      onCheckedChange={(checked) => setPickedItems(prev => ({
                        ...prev,
                        [item.sku || idx]: checked
                      }))}
                      className="w-6 h-6"
                    />
                    {item.image_url && (
                      <img src={item.image_url} alt="" className="w-12 h-12 object-cover rounded" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-wt-text-primary truncate">{item.name || item.title}</p>
                      <p className="text-xs text-wt-text-muted">Qty: {item.quantity} {item.sku && `• SKU: ${item.sku}`}</p>
                    </div>
                  </label>
                ))}
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <button 
                  onClick={() => setShowPickConfirm(false)}
                  className="py-4 px-6 text-lg font-semibold rounded-xl bg-wt-surface border-2 border-wt-border text-wt-text-primary active:scale-95"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => handleDriverAction('pick', { picked_items: pickedItems })}
                  disabled={loading}
                  className="py-4 px-6 text-lg font-semibold rounded-xl bg-purple-500 text-white active:scale-95"
                >
                  {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : 'Confirm Picked'}
                </button>
              </div>
            </div>
          </div>
        );

      case 'picked':
        // After picking, driver marks as "Out for Delivery"
        if (needsReturnCollection) {
          return (
            <button 
              onClick={() => setShowReturnCollection(true)}
              className={`${buttonBaseClass} bg-gradient-to-r from-amber-500 to-orange-500 text-white`}
            >
              <div className="flex items-center justify-center gap-3">
                <RotateCcw className="w-8 h-8" />
                <span>COLLECT RETURNS</span>
              </div>
            </button>
          );
        }
        
        return (
          <button 
            onClick={() => handleDriverAction('out_for_delivery')}
            disabled={loading}
            className={`${buttonBaseClass} bg-gradient-to-r from-cyan-500 to-cyan-600 text-white`}
          >
            {loading ? (
              <Loader2 className="w-8 h-8 animate-spin mx-auto" />
            ) : (
              <div className="flex items-center justify-center gap-3">
                <Truck className="w-8 h-8" />
                <span>OUT FOR DELIVERY</span>
              </div>
            )}
          </button>
        );

      case 'out_for_delivery':
      case 'on_the_way':
        if (needsReturnCollection) {
          return (
            <button 
              onClick={() => setShowReturnCollection(true)}
              className={`${buttonBaseClass} bg-gradient-to-r from-amber-500 to-orange-500 text-white`}
            >
              <div className="flex items-center justify-center gap-3">
                <RotateCcw className="w-8 h-8" />
                <span>COLLECT RETURNS</span>
              </div>
            </button>
          );
        }
        
        // Always show delivery confirmation with POD upload
        if (!showDeliverConfirm) {
          return (
            <button 
              onClick={() => setShowDeliverConfirm(true)}
              className={`${buttonBaseClass} bg-gradient-to-r from-green-500 to-green-600 text-white`}
            >
              <div className="flex items-center justify-center gap-3">
                <Truck className="w-8 h-8" />
                <span>MARK AS DELIVERED</span>
              </div>
            </button>
          );
        }

        // Delivery confirmation screen with POD and optional cash
        return (
          <div className="space-y-4">
            <div className="bg-green-500/10 rounded-2xl p-6 border-2 border-green-500/30">
              <div className="flex items-center gap-3 mb-4">
                <Truck className="w-10 h-10 text-green-400" />
                <p className="text-lg font-bold text-wt-text-primary">Confirm Delivery</p>
              </div>
              
              {/* GPS — required to confirm delivery */}
              <div className="mb-4 p-4 bg-blue-500/10 rounded-xl border border-blue-500/30">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-blue-400" />
                    <span className="font-semibold text-wt-text-primary">Delivery Location</span>
                  </div>
                  {deliveryLocation ? (
                    <Badge className="bg-green-500/20 text-green-400 border border-green-500/30">
                      ✓ Captured
                    </Badge>
                  ) : (
                    <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">
                      Required
                    </Badge>
                  )}
                </div>
                {deliveryLocation ? (
                  <div className="text-xs text-wt-text-muted font-mono break-all" data-testid="driver-gps-coords">
                    {deliveryLocation.lat.toFixed(6)}, {deliveryLocation.lng.toFixed(6)}
                    {deliveryLocation.accuracy && (
                      <span className="ml-2 text-wt-text-secondary">±{Math.round(deliveryLocation.accuracy)}m</span>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-wt-text-secondary">
                    We need your GPS location to stamp where this parcel was delivered.
                  </p>
                )}
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await captureDeliveryLocation();
                      toast.success('Location captured');
                    } catch (e) {
                      toast.error(e.message);
                    }
                  }}
                  disabled={capturingLocation}
                  data-testid="capture-gps-btn"
                  className="mt-3 w-full py-2 px-4 rounded-lg text-sm font-semibold border-2 border-blue-500/40 text-blue-300 active:bg-blue-500/20 disabled:opacity-60"
                >
                  {capturingLocation ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Getting your location…
                    </span>
                  ) : deliveryLocation ? 'Re-capture Location' : 'Capture My Location'}
                </button>
              </div>

              {/* POD Upload - Always shown, multiple photos supported */}
              <div className="mb-4">
                <p className="text-sm text-wt-text-secondary mb-2">Proof of Delivery (Required) — up to 6 photos</p>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleImageUpload(e, 'pod')}
                  className="hidden"
                  id="pod-delivery"
                />
                {podImages.length > 0 && (
                  <div className="grid gap-2 mb-3" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }} data-testid="pod-images-grid">
                    {podImages.map((img, idx) => (
                      <div key={idx} className="relative">
                        <img src={img} alt={`POD ${idx + 1}`} className="w-full h-24 object-cover rounded-lg border border-green-500/50" />
                        <button
                          onClick={() => setPodImages(prev => prev.filter((_, i) => i !== idx))}
                          className="absolute -top-1.5 -right-1.5 bg-red-500 rounded-full flex items-center justify-center"
                          style={{ width: 20, height: 20, minWidth: 20, minHeight: 20, padding: 0 }}
                          data-testid={`pod-remove-${idx}`}
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {podImages.length < 6 && (
                  <label 
                    htmlFor="pod-delivery"
                    className={`flex items-center justify-center gap-3 p-4 border-2 border-dashed rounded-xl active:bg-wt-surface cursor-pointer ${podImages.length ? 'border-green-500' : 'border-wt-border'}`}
                    data-testid="pod-upload-label"
                  >
                    <Camera className="w-8 h-8 text-wt-text-muted" />
                    <span className="text-base text-wt-text-muted">
                      {podImages.length ? 'Add another photo' : 'Take Photo or Select from Gallery'}
                    </span>
                  </label>
                )}
              </div>
              
              {/* Partial delivery — customer rejected some items */}
              {(order.items || []).length > 0 && (
                <div className="mb-4 p-4 bg-orange-500/10 rounded-xl border border-orange-500/30">
                  <button
                    type="button"
                    onClick={() => setShowPartial(!showPartial)}
                    className="flex items-center justify-between w-full"
                    data-testid="toggle-partial-delivery"
                  >
                    <span className="font-semibold text-wt-text-primary text-sm">Customer rejected some items?</span>
                    <span className="text-orange-400 text-sm font-semibold">{showPartial ? 'Hide' : 'Select items'}</span>
                  </button>
                  {showPartial && (
                    <div className="mt-3 space-y-2">
                      {(order.items || []).map((item, idx) => {
                        const maxQ = parseInt(item.quantity) || 1;
                        const q = rejectedQty[idx] || 0;
                        return (
                          <div key={idx} className="flex items-center justify-between gap-2 p-2 bg-wt-bg rounded-lg flex-nowrap">
                            <span className="text-sm text-wt-text-primary flex-1 min-w-0 truncate">{item.product_name || item.name || item.title}</span>
                            <div className="flex items-center gap-2 shrink-0">
                              <button type="button" onClick={() => setRejectedQty(p => ({ ...p, [idx]: Math.max(0, (p[idx] || 0) - 1) }))} className="w-8 h-8 rounded-lg bg-wt-surface border border-wt-border text-wt-text-primary" data-testid={`reject-minus-${idx}`}>−</button>
                              <span className={`w-12 text-center text-sm font-semibold ${q > 0 ? 'text-orange-400' : 'text-wt-text-muted'}`}>{q}/{maxQ}</span>
                              <button type="button" onClick={() => setRejectedQty(p => ({ ...p, [idx]: Math.min(maxQ, (p[idx] || 0) + 1) }))} className="w-8 h-8 rounded-lg bg-wt-surface border border-wt-border text-wt-text-primary" data-testid={`reject-plus-${idx}`}>+</button>
                            </div>
                          </div>
                        );
                      })}
                      <p className="text-[11px] text-wt-text-muted">Rejected items stay with you — a return is created automatically for the warehouse.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Cash collection - Only shown for COD or reopened orders */}
              {needsCash && !fullyPaid && (
                <div className="mb-4 p-4 bg-yellow-500/10 rounded-xl border border-yellow-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <DollarSign className="w-6 h-6 text-yellow-400" />
                    <span className="font-semibold text-wt-text-primary">Cash to Collect</span>
                    <span className="text-xl font-bold text-yellow-400 ml-auto">{formatCurrency(cashAmountDue)}</span>
                  </div>
                  {linkPaid > 0 && !order.is_reopened && (
                    <p className="text-xs text-green-400 mb-2" data-testid="partial-link-paid-note">
                      {formatCurrency(linkPaid)} already paid via payment link — collect the balance {formatCurrency(cashAmountDue)} in cash
                    </p>
                  )}
                  {rejectedValue > 0 && !order.is_reopened && (
                    <p className="text-xs text-orange-400 mb-2" data-testid="rejected-value-note">
                      Reduced by {formatCurrency(rejectedValue)} for rejected items — collect only {formatCurrency(cashAmountDue)}
                    </p>
                  )}
                  <Input
                    type="number"
                    inputMode="decimal"
                    placeholder="Enter amount collected"
                    value={cashAmount}
                    onChange={(e) => setCashAmount(e.target.value)}
                    className="h-12 text-lg text-center bg-wt-bg border-2 border-wt-border rounded-xl mb-3"
                  />
                  
                  {/* MamoPay Payment Link Option */}
                  <div className="pt-3 border-t border-yellow-500/30">
                    <p className="text-sm text-wt-text-muted mb-2 text-center">Customer doesn't have cash?</p>
                    <PaymentLinkButton
                      referenceType="order"
                      referenceId={order.id}
                      amount={cashAmountDue}
                      customerName={`${order.shipping_address?.first_name || ''} ${order.shipping_address?.last_name || ''}`.trim()}
                      customerEmail={order.customer?.email}
                      customerPhone={order.shipping_address?.phone || order.customer?.phone}
                      title={`Order ${order.order_number || order.id}`}
                      existingLinks={order.payment_links || []}
                      variant="driver"
                      onLinkCreated={() => onOrderUpdate && onOrderUpdate()}
                      onPaymentConfirmed={() => {
                        toast.success('Payment confirmed via MamoPay!');
                        onOrderUpdate && onOrderUpdate();
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Show MamoPay paid badge */}
              {fullyPaid && order.mamopay_paid && (
                <div className="mb-4 p-4 bg-green-500/10 rounded-xl border border-green-500/30">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-6 h-6 text-green-400" />
                    <span className="font-semibold text-green-400">Paid via MamoPay</span>
                    <CheckCircle className="w-5 h-5 text-green-400 ml-auto" />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <button 
                  onClick={() => {
                    setShowDeliverConfirm(false);
                    setPodImages([]);
                    setCashAmount('');
                  }}
                  className="py-4 px-6 text-lg font-semibold rounded-xl bg-wt-surface border-2 border-wt-border text-wt-text-primary active:scale-95"
                >
                  Cancel
                </button>
                <button 
                  onClick={async () => {
                    if (podImages.length === 0) {
                      toast.error('Please take a delivery photo');
                      return;
                    }

                    // GPS is required — try to capture now if not already.
                    let loc = deliveryLocation;
                    if (!loc) {
                      try {
                        loc = await captureDeliveryLocation();
                      } catch (e) {
                        toast.error(e.message);
                        return;
                      }
                    }

                    // Skip cash requirement only when the order is FULLY paid
                    const skipCash = fullyPaid;
                    
                    // Validate cash amount for COD orders
                    if (needsCash && !skipCash) {
                      const enteredAmount = parseFloat(cashAmount);
                      if (!cashAmount || isNaN(enteredAmount) || enteredAmount <= 0) {
                        toast.error('Please enter the cash amount collected');
                        return;
                      }
                      // Warn if amount is significantly different from expected
                      if (Math.abs(enteredAmount - cashAmountDue) > 0.5) {
                        if (!window.confirm(`You entered ${formatCurrency(enteredAmount)} but expected ${formatCurrency(cashAmountDue)}. Continue?`)) {
                          return;
                        }
                      }
                    }
                    
                    // Partial delivery: build rejected items list
                    const rejected_items = Object.entries(rejectedQty)
                      .filter(([, q]) => q > 0)
                      .map(([idx, q]) => {
                        const it = order.items[idx] || {};
                        return {
                          sku: it.sku || '',
                          name: it.product_name || it.name || it.title || '',
                          quantity: q,
                          quantity_ordered: parseInt(it.quantity) || 1,
                          price: parseFloat(it.price) || 0,
                          variant_title: it.variant_title || '',
                          image_url: it.image_url || it.image || '',
                        };
                      });
                    const totalQty = (order.items || []).reduce((s, it) => s + (parseInt(it.quantity) || 1), 0);
                    const rejectedTotal = rejected_items.reduce((s, it) => s + it.quantity, 0);
                    if (rejectedTotal > 0 && rejectedTotal >= totalQty) {
                      toast.error('All items rejected — use "Unable to Deliver" instead of partial delivery');
                      return;
                    }

                    handleDriverAction('deliver', {
                      pod_image: podImages[0],
                      pod_images: podImages,
                      delivery_latitude: loc.lat,
                      delivery_longitude: loc.lng,
                      delivery_accuracy: loc.accuracy,
                      ...(rejected_items.length > 0 && { rejected_items }),
                      ...((needsCash && !skipCash) && {
                        cash_collected: true,
                        cash_amount: parseFloat(cashAmount)
                      }),
                      ...(skipCash && {
                        mamopay_paid: true
                      })
                    });
                  }}
                  disabled={loading || podImages.length === 0 || capturingLocation}
                  className={`py-4 px-6 text-lg font-semibold rounded-xl text-white active:scale-95 ${(podImages.length > 0 && !capturingLocation) ? 'bg-green-500' : 'bg-gray-400'}`}
                  data-testid="confirm-delivery-btn"
                >
                  {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : 'Confirm Delivery'}
                </button>
              </div>
            </div>
          </div>
        );

      case 'delivery_rescheduled':
        return (
          <div className="space-y-4">
            <div className="bg-amber-500/10 rounded-2xl p-5 border-2 border-amber-500/30" data-testid="driver-rescheduled-card">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-8 h-8 text-amber-400" />
                <p className="text-lg font-bold text-amber-400">Delivery Rescheduled</p>
              </div>
              <p className="text-sm text-wt-text-secondary">
                Reason: {(RESCHEDULE_REASONS.find(r => r.value === order.reschedule_reason)?.label) || (order.reschedule_reason || '').replace(/_/g, ' ')}
              </p>
              {order.rescheduled_delivery_date && (
                <p className="text-sm text-wt-text-secondary mt-1">
                  New delivery date: <span className="font-semibold text-amber-300">{order.rescheduled_delivery_date}</span>
                </p>
              )}
              <p className="text-xs text-wt-text-muted mt-1">Attempt {order.delivery_attempt_count || 1} of 3</p>
            </div>
            <button
              onClick={() => handleDriverAction('out_for_delivery')}
              disabled={loading}
              className={`${buttonBaseClass} bg-gradient-to-r from-cyan-500 to-cyan-600 text-white`}
              data-testid="driver-retry-delivery-btn"
            >
              {loading ? (
                <Loader2 className="w-8 h-8 animate-spin mx-auto" />
              ) : (
                <div className="flex items-center justify-center gap-3">
                  <Truck className="w-8 h-8" />
                  <span>OUT FOR DELIVERY (RETRY)</span>
                </div>
              )}
            </button>
          </div>
        );

      case 'delivered':
      case 'fulfilled':
        return (
          <div className="bg-green-500/10 rounded-2xl p-8 border-2 border-green-500/30 text-center" data-testid="driver-delivered-card">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-3" />
            <p className="text-xl font-bold text-green-400">
              {status === 'fulfilled' ? 'Order Fulfilled!' : 'Order Delivered!'}
            </p>
            {order.driver_delivery_location && order.driver_delivery_location.lat && (
              <div className="mt-4 p-4 bg-wt-bg/40 rounded-xl border border-green-500/20 text-left" data-testid="driver-delivery-stamp">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-green-400" />
                  <span className="text-sm font-semibold text-wt-text-primary">Delivered at</span>
                  {order.driver_delivery_location.accuracy_m && (
                    <Badge className="ml-auto bg-green-500/15 text-green-300 border-green-500/30 text-[10px]">
                      ±{Math.round(order.driver_delivery_location.accuracy_m)}m
                    </Badge>
                  )}
                </div>
                {order.driver_delivery_location.address && (
                  <p className="text-sm text-wt-text-secondary mb-1">
                    {order.driver_delivery_location.address}
                  </p>
                )}
                <p className="text-xs font-mono text-wt-text-muted">
                  {Number(order.driver_delivery_location.lat).toFixed(6)}, {Number(order.driver_delivery_location.lng).toFixed(6)}
                </p>
                <a
                  href={`https://www.google.com/maps?q=${order.driver_delivery_location.lat},${order.driver_delivery_location.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-3 text-sm text-blue-300 hover:underline"
                  data-testid="driver-delivery-map-link"
                >
                  <MapPin className="w-3 h-3" /> View on Google Maps
                </a>
              </div>
            )}
          </div>
        );

      case 'failed':
        return (
          <div className="bg-red-500/10 rounded-2xl p-8 border-2 border-red-500/30 text-center">
            <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-3" />
            <p className="text-xl font-bold text-red-400">Delivery Failed</p>
            {order.failure_reason && (
              <p className="text-sm text-wt-text-muted mt-2">{order.failure_reason}</p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Reschedule delivery option
  const renderRescheduleButton = () => {
    if (!['accepted', 'picked', 'out_for_delivery', 'on_the_way'].includes(status)) return null;
    if ((order.delivery_attempt_count || 0) >= 3) return null;

    if (showReschedule) {
      return (
        <div className="mt-4 bg-amber-500/10 rounded-2xl p-5 border-2 border-amber-500/30" data-testid="reschedule-form">
          <p className="text-lg font-semibold text-amber-400 mb-3">Reschedule Delivery</p>
          <label className="text-sm text-wt-text-secondary">Reason *</label>
          <select
            value={reschedReason}
            onChange={(e) => setReschedReason(e.target.value)}
            className="w-full mt-1 mb-3 p-3 rounded-xl bg-wt-bg border-2 border-wt-border text-wt-text-primary text-base"
            data-testid="reschedule-reason-select"
          >
            <option value="">Select a reason...</option>
            {RESCHEDULE_REASONS.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          <label className="text-sm text-wt-text-secondary">Preferred new date (optional)</label>
          <DateInput
            value={reschedDate}
            onChange={(e) => setReschedDate(e.target.value)}
            className="w-full mt-1 mb-3 p-3 rounded-xl bg-wt-bg border-2 border-wt-border text-wt-text-primary text-base"
            data-testid="reschedule-date-input"
          />
          <Textarea
            placeholder="Notes (optional)..."
            value={reschedNote}
            onChange={(e) => setReschedNote(e.target.value)}
            className="bg-wt-bg border-2 border-wt-border rounded-xl text-base mb-4"
            rows={2}
            data-testid="reschedule-note-input"
          />
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => { setShowReschedule(false); setReschedReason(''); setReschedNote(''); setReschedDate(''); }}
              className="py-3 text-base font-semibold rounded-xl bg-wt-surface border-2 border-wt-border"
              data-testid="reschedule-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={() => handleDriverAction('reschedule', {
                reason: reschedReason,
                reason_note: reschedNote,
                ...(reschedDate && { preferred_date: reschedDate }),
              })}
              disabled={loading || !reschedReason}
              className="py-3 text-base font-semibold rounded-xl bg-amber-500 text-white disabled:opacity-50"
              data-testid="reschedule-submit-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Reschedule'}
            </button>
          </div>
        </div>
      );
    }

    return (
      <button
        onClick={() => setShowReschedule(true)}
        className="w-full mt-4 py-4 text-base font-semibold rounded-xl border-2 border-amber-500/40 text-amber-400 active:bg-amber-500/10"
        data-testid="reschedule-delivery-btn"
      >
        <Clock className="w-5 h-5 inline mr-2" />
        Reschedule Delivery
      </button>
    );
  };

  // Fail delivery option
  const renderFailButton = () => {
    if (!canMarkFailed) return null;
    if (['delivered', 'fulfilled', 'failed', 'pending'].includes(status)) return null;

    if (showFailReason) {
      return (
        <div className="mt-6 bg-red-500/10 rounded-2xl p-5 border-2 border-red-500/30">
          <p className="text-lg font-semibold text-red-400 mb-3">Why couldn't you deliver?</p>
          <Textarea
            placeholder="Enter reason..."
            value={failReason}
            onChange={(e) => setFailReason(e.target.value)}
            className="bg-wt-bg border-2 border-wt-border rounded-xl text-base mb-4"
            rows={3}
          />
          <div className="grid grid-cols-2 gap-3">
            <button 
              onClick={() => { setShowFailReason(false); setFailReason(''); }}
              className="py-3 text-base font-semibold rounded-xl bg-wt-surface border-2 border-wt-border"
            >
              Cancel
            </button>
            <button 
              onClick={() => handleDriverAction('fail', { reason: failReason })}
              disabled={loading || !failReason}
              className="py-3 text-base font-semibold rounded-xl bg-red-500 text-white disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Submit'}
            </button>
          </div>
        </div>
      );
    }

    return (
      <button 
        onClick={() => setShowFailReason(true)}
        className="w-full mt-6 py-4 text-base font-semibold rounded-xl border-2 border-red-500/30 text-red-400 active:bg-red-500/10"
        data-testid="driver-cant-deliver-btn"
      >
        <AlertTriangle className="w-5 h-5 inline mr-2" />
        Can't Deliver?
      </button>
    );
  };

  // Return collection full-screen modal
  const renderReturnCollectionModal = () => {
    if (!showReturnCollection) return null;

    return (
      <div className="fixed inset-0 bg-wt-bg z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-wt-border bg-wt-surface">
          <h2 className="text-xl font-bold text-wt-text-primary">Collect Return Items</h2>
          <button 
            onClick={() => setShowReturnCollection(false)}
            className="p-2 rounded-full bg-wt-bg"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {/* Pickup Instructions Alert */}
          {order.return_pickup_instructions && (
            <div className="bg-amber-500/10 rounded-2xl p-4 border-2 border-amber-500/30">
              <p className="text-base font-semibold text-amber-400 mb-1">📋 Instructions</p>
              <p className="text-base text-wt-text-secondary">{order.return_pickup_instructions}</p>
            </div>
          )}

          {/* Items Checklist */}
          <div>
            <p className="text-lg font-semibold text-wt-text-primary mb-3">Items to Collect</p>
            <div className="space-y-3">
              {order.return_items_to_collect?.map((item, idx) => (
                <div 
                  key={idx} 
                  onClick={() => setCollectedItems(prev => ({ ...prev, [item.sku]: prev[item.sku] === false ? true : prev[item.sku] !== false ? false : true }))}
                  className={`flex items-center gap-4 p-4 rounded-2xl border-2 active:scale-98 transition-all cursor-pointer
                    ${collectedItems[item.sku] !== false ? 'bg-green-500/10 border-green-500/30' : 'bg-wt-surface border-wt-border'}`}
                >
                  {item.image_url && (
                    <img src={item.image_url} alt={item.name} className="w-16 h-16 object-cover rounded-xl" />
                  )}
                  <div className="flex-1">
                    <p className="text-base font-semibold text-wt-text-primary">{item.name}</p>
                    {item.variant_title && (
                      <p className="text-sm text-wt-text-muted">{item.variant_title}</p>
                    )}
                    <p className="text-sm text-wt-text-muted">Qty: {item.quantity}</p>
                  </div>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center
                    ${collectedItems[item.sku] !== false ? 'bg-green-500' : 'bg-wt-border'}`}>
                    {collectedItems[item.sku] !== false && <Check className="w-5 h-5 text-white" />}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Photo Upload */}
          <div>
            <p className="text-lg font-semibold text-wt-text-primary mb-3">
              📸 Take Photos <span className="text-red-400">*</span>
            </p>
            
            {/* Photo Grid */}
            {returnImages.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-3">
                {returnImages.map((img, idx) => (
                  <div key={idx} className="relative aspect-square">
                    <img src={img} alt={`Return ${idx}`} className="w-full h-full object-cover rounded-xl" />
                    <button
                      onClick={(e) => { e.stopPropagation(); removeReturnImage(idx); }}
                      className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1.5"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, 'return')}
              className="hidden"
              id="return-mobile"
            />
            <label 
              htmlFor="return-mobile"
              className="flex items-center justify-center gap-3 py-8 border-2 border-dashed border-wt-border rounded-2xl active:bg-wt-surface cursor-pointer"
            >
              <Camera className="w-10 h-10 text-wt-accent" />
              <span className="text-lg text-wt-text-secondary">Take Photo or Select from Gallery</span>
            </label>
          </div>

          {/* Notes */}
          <div>
            <p className="text-lg font-semibold text-wt-text-primary mb-2">Notes (Optional)</p>
            <Textarea
              placeholder="Any issues with items..."
              value={returnNotes}
              onChange={(e) => setReturnNotes(e.target.value)}
              className="bg-wt-surface border-2 border-wt-border rounded-xl text-base"
              rows={2}
            />
          </div>
        </div>

        {/* Fixed Bottom Button */}
        <div className="p-4 border-t border-wt-border bg-wt-surface">
          <button 
            onClick={handleReturnCollection}
            disabled={loading || returnImages.length === 0}
            className="w-full py-5 text-xl font-bold rounded-2xl bg-gradient-to-r from-green-500 to-green-600 text-white disabled:opacity-50 active:scale-95"
          >
            {loading ? (
              <Loader2 className="w-8 h-8 animate-spin mx-auto" />
            ) : (
              <div className="flex items-center justify-center gap-2">
                <Check className="w-7 h-7" />
                <span>CONFIRM COLLECTION</span>
              </div>
            )}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 pb-4">
      {/* Order Info Card */}
      <div className="bg-wt-surface rounded-2xl p-4 border border-wt-border">
        <div className="flex items-center justify-between mb-3">
          <span className="text-2xl font-bold text-wt-gold">#{order.order_number}</span>
          <StatusBadge />
        </div>
        
        {/* Customer Quick Info */}
        <div className="space-y-2 text-base">
          <div className="flex items-center gap-3 text-wt-text-secondary">
            <MapPin className="w-5 h-5 text-wt-text-muted flex-shrink-0" />
            <span className="line-clamp-2">
              {typeof order.shipping_address === 'object' 
                ? [order.shipping_address?.address1, order.shipping_address?.city, order.shipping_address?.country].filter(Boolean).join(', ') || 'No address'
                : order.shipping_address || 'No address'}
            </span>
          </div>
          {order.customer_phone && (
            <a href={`tel:${order.customer_phone}`} className="flex items-center gap-3 text-blue-400">
              <Phone className="w-5 h-5 flex-shrink-0" />
              <span>{order.customer_phone}</span>
            </a>
          )}
        </div>
      </div>

      {/* Progress Steps */}
      {!['delivered', 'fulfilled', 'failed'].includes(status) && (
        <div className="bg-wt-surface rounded-2xl p-2 border border-wt-border">
          <ProgressSteps />
        </div>
      )}

      {/* Return Order Alert */}
      {isReturnOrder && (
        <div className="bg-amber-500/10 rounded-2xl p-4 border-2 border-amber-500/30">
          <div className="flex items-center gap-3 mb-2">
            <RotateCcw className="w-7 h-7 text-amber-400" />
            <span className="text-lg font-bold text-amber-400">
              {order.return_replacement_type?.replace(/_/g, ' ').toUpperCase()}
            </span>
          </div>
          <p className="text-base text-wt-text-secondary mb-2">
            Reason: {order.return_replacement_reason?.replace(/_/g, ' ')}
          </p>
          {needsReturnCollection ? (
            <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-sm py-1 px-3">
              ⚠️ Collect return items from customer
            </Badge>
          ) : order.return_items_collected && (
            <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-sm py-1 px-3">
              ✓ Return items collected
            </Badge>
          )}
        </div>
      )}

      {/* Reopen Cash Alert */}
      {order.is_reopened && order.reopen_cash_required && !order.reopen_cash_collected && (
        <div className="bg-blue-500/10 rounded-2xl p-4 border-2 border-blue-500/30">
          <div className="flex items-center gap-3">
            <DollarSign className="w-7 h-7 text-blue-400" />
            <div>
              <p className="text-base font-semibold text-blue-400">Cash to Collect</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(order.reopen_cash_amount)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Action Button */}
      {renderActionButton()}
      
      {/* Reschedule Button */}
      {renderRescheduleButton()}
      
      {/* Fail Button */}
      {renderFailButton()}

      {/* Return Collection Modal */}
      {renderReturnCollectionModal()}
    </div>
  );
};

export default DriverActionPanel;
